import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/shared/ui/icon';

import type { ListPageController } from '../model/useListPageController';

/**
 * Náhledový drawer (anatomie region 8) — pravý overlay panel nad pracovní oblastí. Obsah dodává
 * šablona (`config.preview`); seznam zůstává mounted pod vrstvou, takže návrat je beze změny
 * (filtry/stránka/výběr/scroll). Zavírá ✕, klik na overlay i Esc.
 */
export function PreviewDrawer<TItem>({
  controller,
  render,
}: {
  controller: ListPageController<TItem>;
  render?: (item: TItem) => ReactNode;
}) {
  const { t } = useTranslation('listPage');
  const item = controller.previewItem;
  const { closePreview } = controller;

  useEffect(() => {
    if (!item) {
      return;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closePreview();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [item, closePreview]);

  if (!render || !item) {
    return null;
  }

  return (
    <>
      <div className="phxlist-drawer-overlay" onClick={closePreview} />
      <aside className="phxlist-drawer" role="dialog" aria-modal="true">
        <div className="phxlist-drawer-head">
          <span className="phxlist-drawer-title">{t('preview.title')}</span>
          <button
            type="button"
            className="phxlist-drawer-close"
            title={t('preview.close')}
            onClick={closePreview}
          >
            <Icon name="x" size={14} />
          </button>
        </div>
        <div className="phxlist-drawer-body">{render(item)}</div>
      </aside>
    </>
  );
}
